import type { PromoRow } from '@/services/promos';

import {
  CreatePromoRequest,
  IsoDateString,
  ProgramType,
  PromoParty,
  PromoType,
  PromoUtilityScope,
  PromoValueType,
  UpdatePromoRequest,
} from 'types';

export interface PromoFormValues {
  code: string;
  name: string;
  states: string[];
  lseIds: string[];
  validFrom: string;
  validThrough: string;
  valueType: PromoValueType | '';
  value: string;
  type: PromoType | '';
  collectableBy: PromoParty[];
  programTypes: ProgramType[];
}

export const defaultPromoFormValues: PromoFormValues = {
  code: '',
  name: '',
  states: [],
  lseIds: [],
  validFrom: '',
  validThrough: '',
  valueType: '',
  value: '',
  type: '',
  collectableBy: [],
  programTypes: [],
};

/** An lseId can serve more than one state, so a selection is keyed by state and lseId together. */
export const toUtilityOptionValue = (state: string, lseId: number): string => `${state}:${lseId}`;

const fromUtilityOptionValue = (value: string): number => Number(value.slice(value.indexOf(':') + 1));

const cleanFloat = (n: number): number => Number(n.toPrecision(12));

export const promoToFormValues = (promo: PromoRow): PromoFormValues => {
  const { type, value } = promo.valueRule;
  return {
    code: promo.code,
    name: promo.name,
    states: promo.utilities.map(({ state }) => state),
    lseIds: promo.utilities.flatMap(({ state, lseIds }) =>
      lseIds.map((lseId) => toUtilityOptionValue(state, lseId)),
    ),
    validFrom: promo.validFrom,
    validThrough: promo.validThrough ?? '',
    valueType: type,
    value: String(type === PromoValueType.percentOfCost ? cleanFloat(value * 100) : value),
    type: promo.type,
    collectableBy: [...promo.collectableBy],
    programTypes: [...promo.programTypes],
  };
};

const normalizeStates = (states: string[]): string[] => states.map((state) => state.trim().toUpperCase());

/** Maps a utility option value (see toUtilityOptionValue) to its state, so the flat selection can be regrouped. */
export type StateByLseId = ReadonlyMap<string, string>;

/**
 * The form edits two flat lists; the API wants lseIds grouped under their state. A state with no
 * selected utility is kept as an empty group so validation, not this mapping, decides what to do.
 */
export const groupUtilitiesByState = (
  states: string[],
  lseIds: string[],
  stateByLseId: StateByLseId,
): PromoUtilityScope[] =>
  normalizeStates(states).map((state) => ({
    state,
    lseIds: lseIds.filter((value) => stateByLseId.get(value) === state).map(fromUtilityOptionValue),
  }));

const toValueRuleValue = (value: string, valueType: PromoValueType | ''): number => {
  const parsed = Number(value);
  return valueType === PromoValueType.percentOfCost ? cleanFloat(parsed / 100) : parsed;
};

export const toCreatePromoRequest = (values: PromoFormValues, stateByLseId: StateByLseId): CreatePromoRequest => ({
  type: values.type as PromoType,
  code: values.code.trim(),
  name: values.name.trim(),
  utilities: groupUtilitiesByState(values.states, values.lseIds, stateByLseId),
  programTypes: values.programTypes,
  ...(values.type === PromoType.incentive ? { collectableBy: values.collectableBy } : {}),
  valueRule: {
    type: values.valueType as PromoValueType,
    value: toValueRuleValue(values.value, values.valueType),
  },
  validFrom: values.validFrom as IsoDateString,
  ...(values.validThrough ? { validThrough: values.validThrough as IsoDateString } : {}),
});

export const toUpdatePromoRequest = (values: PromoFormValues, stateByLseId: StateByLseId): UpdatePromoRequest => ({
  name: values.name.trim(),
  utilities: groupUtilitiesByState(values.states, values.lseIds, stateByLseId),
  ...(values.validThrough ? { validThrough: values.validThrough as IsoDateString } : {}),
});

export const validateValidThrough = (
  validThrough: string,
  validFrom: string,
  { hadValidThrough }: { hadValidThrough: boolean },
): string | undefined => {
  if (!validThrough) {
    return hadValidThrough ? 'Valid through cannot be cleared once set' : undefined;
  }

  if (validFrom && validThrough < validFrom) return 'Valid through cannot be before valid from';
  return undefined;
};

export const validatePromoType = (type: PromoType | ''): string | undefined =>
  type ? undefined : 'Promo type is required';

export const validateCollectableBy = (collectableBy: PromoParty[], isIncentive: boolean): string | undefined =>
  isIncentive && collectableBy.length === 0 ? 'Select at least one party' : undefined;

export const validateProgramTypes = (programTypes: ProgramType[]): string | undefined =>
  programTypes.length > 0 ? undefined : 'Select at least one program type';

export const validateStates = (states: string[]): string | undefined =>
  states.length > 0 ? undefined : 'Select at least one state';

export const validateLseIds = (
  lseIds: string[],
  states: string[],
  stateByLseId: StateByLseId,
): string | undefined => {
  if (states.length === 0) return lseIds.length > 0 ? undefined : 'Select at least one utility';
  const covered = new Set(lseIds.map((lseId) => stateByLseId.get(lseId)));
  return normalizeStates(states).every((state) => covered.has(state))
    ? undefined
    : 'Select at least one utility for each selected state';
};

export const validateValueType = (valueType: PromoValueType | ''): string | undefined =>
  valueType ? undefined : 'Value type is required';

export const validateValue = (value: string, valueType: PromoValueType | ''): string | undefined => {
  if (value.trim() === '') return 'Enter a value';
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 'Enter a valid number';
  if (parsed < 0) return 'Value cannot be negative';
  if (valueType === PromoValueType.percentOfCost && parsed > 100) return 'Percent cannot be more than 100';
  return undefined;
};

export const toIsoDateString = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const fromIsoDateString = (day: string): Date | undefined => (day ? new Date(`${day}T00:00:00`) : undefined);
